import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, find } from 'lodash';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import { compose } from 'recompose';
import {
  Tab, Loader, Dimmer, Message, Confirm,
} from 'semantic-ui-react';
import moment from 'moment';
import DealerQuote from './DealerQuote';
import EnrollmentStatus from './EnrollmentStatus';
import AutoPsi from './AutoPsi';
import enrollmentStatusQuery from '../graphql/enrollmentStatusQuery';
import validateLocallyLinkedDealerQuery from '../graphql/validateLocallyLinkedDealerQuery';
import createLocallyLinkedDealerMutation from '../graphql/createLocallyLinkedDealerMutation';
import deleteLocallyLinkedDealerMutation from '../graphql/deleteLocallyLinkedDealerMutation';
import client from '../apollo';
import { PRODUCT_FAMILY_ALL } from '../constants/productFamily';
import ProductSelection from './ProductSelection';

const DealerDashboard = props => {
  const { createMutation, deleteMutation } = props;

  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [auctionAccessNumber, setAuctionAccessNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [showProductSelectionWindow, setShowProductSelectionWindow] = useState(false);
  const [selectedDealerNo, setSelectedDealerNo] = useState(null);
  const [selectedDealerName, setSelectedDealerName] = useState(null);
  const [chosenIndex, setChosenIndex] = useState('');
  const [chosenOffer, setChosenOffer] = useState([]);
  const [isRevise, setIsRevise] = useState(false);
  const [handleLink, setHandleLink] = useState(false);
  const [handleLinkData, setHandleLinkData] = useState({});
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [localDealerNo, setLocalDealerNo] = useState('');
  const [localDealerName, setLocalDealerName] = useState('');
  const [unlinkDealer, setUnlinkDealer] = useState(false);
  const [dealerNo, setDealerNo] = useState(null);

  const isEnrollmentStatusIncludeDealer = useCallback((status, dealerNumber) => (
    status
      && status.enrollments.filter(
        offer => offer.dealerNo === dealerNumber
      ).length > 0
  ), []);

  const displayRemainingDealers = useCallback(() => {
    if (!enrollmentStatus) return;
    const enrollments = [...enrollmentStatus.enrollments];
    const index = enrollments.indexOf(
      find(enrollments, ['dealerNo', dealerNo])
    );
    if (index > -1) enrollments.splice(index, 1);
    setEnrollmentStatus({ ...enrollmentStatus, enrollments });
  }, [enrollmentStatus, dealerNo]);

  const removeDealerHandler = useCallback(() => {
    const input = {
      hundredmil: parseInt(auctionAccessNumber, 10),
      dealerNo: parseInt(dealerNo, 10),
    };

    deleteMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          deleteLocallyLinkedDealer: { success, errors },
        } = data;
        if (success) {
          setFlashMessage(null);
          setLoading(false);
          setUnlinkDealer(false);
          displayRemainingDealers();
        } else {
          setFlashMessage(errors);
          setLoading(false);
          setUnlinkDealer(false);
        }
      })
      .catch(error => {
        setLoading(false);
        setFlashMessage(error.message || 'Error deleting dealer');
        setUnlinkDealer(false);
      });
  }, [deleteMutation, auctionAccessNumber, dealerNo, displayRemainingDealers]);

  const addDealerHandler = useCallback(() => {
    if (isEmpty(auctionAccessNumber)) {
      setFlashMessage('HundredMil must be valid');
    } else {
      if (
        isEnrollmentStatusIncludeDealer(
          enrollmentStatus,
          parseInt(localDealerNo, 10)
        ) === true
      ) {
        setFlashMessage('Dealer is already linked to HundredMil');
        return;
      }
      setLoading(true);
      setFlashMessage('');
      client
        .query({
          query: validateLocallyLinkedDealerQuery,
          variables: { dealerNo: localDealerNo },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const {
            validateLocallyLinkedDealer: { status, dealerInformation },
          } = data;
          if (status === 'valid') {
            setLoading(false);
            setLocalDealerName(dealerInformation.dealerName);
          } else {
            setLoading(false);
            setFlashMessage(status);
          }
        });
    }
  }, [auctionAccessNumber, enrollmentStatus, localDealerNo, isEnrollmentStatusIncludeDealer]);

  const showProductSelection = useCallback((dealerNumber, name, revise, upgrade) => {
    setShowProductSelectionWindow(true);
    setSelectedDealerNo(dealerNumber);
    setSelectedDealerName(name);
    setIsRevise(revise);
    setIsUpgrade(upgrade);
  }, []);

  const onSearchSubmitHandler = useCallback(() => {
    setLoading(true);
    setFlashMessage('');
    setEnrollmentStatus(null);
    client
      .query({
        query: enrollmentStatusQuery,
        variables: { auctionAccessNumber, productFamily: PRODUCT_FAMILY_ALL },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const { enrollmentStatus: status } = data;

        setEnrollmentStatus(status);
        setLoading(false);
        setFlashMessage('');

        if (handleLink) {
          showProductSelection(
            handleLinkData.dealerNo,
            handleLinkData.dealershipName,
            isRevise,
            isUpgrade
          );
        }
      })
      .catch(() => {
        setLoading(false);
        setFlashMessage('Network Error..');
      });
  }, [auctionAccessNumber, handleLink, handleLinkData, isRevise, isUpgrade, showProductSelection]);

  const displayDealerConfirm = useCallback(dealerNumber => {
    setUnlinkDealer(true);
    setDealerNo(dealerNumber);
  }, []);

  const filteredOffers = useCallback((dealerNumber, status) => (
    status
      && status.eligibleOffers.filter(
        offer => offer.dealerNo === dealerNumber
      )
  ), []);

  const enrollmentDetails = useCallback((dealerNumber, status) => (
    status
      && status.enrollments.filter(
        enrollment => enrollment.dealerNo === dealerNumber
      )
  ), []);

  const handleRadioButtonChange = useCallback(index => {
    setChosenIndex(index);
    setChosenOffer(filteredOffers(selectedDealerNo, enrollmentStatus)[index]);
  }, [selectedDealerNo, enrollmentStatus, filteredOffers]);

  const handleProductSelection = useCallback((email, phone) => {
    if (!enrollmentStatus) {
      setShowProductSelectionWindow(false);
      setChosenIndex('');
      setChosenOffer([]);
      setSelectedDealerNo('');
      setHandleLink(false);
      setHandleLinkData({});
      return;
    }
    const enrollments = [...enrollmentStatus.enrollments];
    const enrollment = { ...find(enrollments, ['dealerNo', selectedDealerNo]) };
    const index = enrollments.indexOf(
      find(enrollments, ['dealerNo', selectedDealerNo])
    );
    if (index > -1) {
      enrollment.incompleteEnrollment = {
        dealerNo: selectedDealerNo,
        sendAt: moment(new Date()).format('MM/DD/YYYY'),
      };
      enrollments[index] = enrollment;
    }
    setShowProductSelectionWindow(false);
    setEnrollmentStatus({
      ...enrollmentStatus, email, phone, enrollments,
    });
    setChosenIndex('');
    setChosenOffer([]);
    setSelectedDealerNo('');
    setHandleLink(false);
    setHandleLinkData({});
  }, [enrollmentStatus, selectedDealerNo]);

  const handleCancel = useCallback(() => {
    setShowProductSelectionWindow(false);
    setChosenIndex('');
    setChosenOffer([]);
    setSelectedDealerNo('');
    setHandleLink(false);
    setHandleLinkData({});
    onSearchSubmitHandler();
  }, [onSearchSubmitHandler]);

  const handlePendingAcceptanceLink = useCallback((
    auctionAccessNum,
    dealerNumber,
    dealershipName,
    revise
  ) => {
    setAuctionAccessNumber(auctionAccessNum);
    setHandleLink(true);
    setHandleLinkData({
      dealerNo: parseInt(dealerNumber, 10),
      dealershipName,
    });
    setIsRevise(revise);
    // We need to trigger search after state update, but since setState is async,
    // we can't rely on callback in hooks.
    // However, onSearchSubmitHandler depends on auctionAccessNumber which is updated.
    // But onSearchSubmitHandler uses the state value.
    // A better way is to pass the value directly or use useEffect.
    // For now, let's call it directly with the new value if we refactor onSearchSubmitHandler to accept args.
    // But onSearchSubmitHandler uses state.
    // Let's use a useEffect to trigger search if handleLink is true and auctionAccessNumber changes?
    // Or just call a modified search handler.
    // Let's try to call it in a timeout or use a ref to track if we need to search.
    // Actually, the original code used callback of setState.
    // Here we can just call the logic directly.

    // Re-implementing logic of onSearchSubmitHandler here to avoid stale state issues or complex deps
    setLoading(true);
    setFlashMessage('');
    setEnrollmentStatus(null);
    client
      .query({
        query: enrollmentStatusQuery,
        variables: { auctionAccessNumber: auctionAccessNum, productFamily: PRODUCT_FAMILY_ALL },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const { enrollmentStatus: status } = data;

        setEnrollmentStatus(status);
        setLoading(false);
        setFlashMessage('');

        showProductSelection(
          parseInt(dealerNumber, 10),
          dealershipName,
          revise,
          isUpgrade // Note: isUpgrade might be stale here if not passed
        );
      })
      .catch(() => {
        setLoading(false);
        setFlashMessage('Network Error..');
      });
  }, [isUpgrade, showProductSelection]);

  const handleClose = useCallback(() => {
    setLocalDealerName('');
    setUnlinkDealer(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setLoading(true);
    setLocalDealerName('');

    const input = {
      hundredmil: parseInt(auctionAccessNumber, 10),
      dealerNo: parseInt(localDealerNo, 10),
    };

    createMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          createLocallyLinkedDealer: { success, errors },
        } = data;
        if (success) {
          setFlashMessage(null);
          setLoading(false);
          setLocalDealerNo('');
          onSearchSubmitHandler();
        } else {
          setFlashMessage(errors);
          setLoading(false);
          setLocalDealerNo('');
        }
      })
      .catch(error => {
        setLoading(false);
        setFlashMessage(error.message || 'Error creating dealer');
      });
  }, [createMutation, auctionAccessNumber, localDealerNo, onSearchSubmitHandler]);

  const onChangeHandler = useCallback(field => event => {
    if (field === 'auctionAccessNumber') setAuctionAccessNumber(event.target.value);
    if (field === 'localDealerNo') setLocalDealerNo(event.target.value);
  }, []);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const offers = filteredOffers(selectedDealerNo, enrollmentStatus);
  const auctionAccessName = enrollmentStatus && enrollmentStatus.name;
  const email = enrollmentStatus && enrollmentStatus.email;
  const phone = enrollmentStatus && enrollmentStatus.phone;
  const enrollment = enrollmentDetails(
    selectedDealerNo,
    enrollmentStatus
  );

  const panes = [
    {
      menuItem: 'DEALER QUOTE',
      render: () => (
        <Tab.Pane>
          {' '}
          {flashMessage && (
            <Message
              onDismiss={handleDismiss}
              content={flashMessage}
              negative
            />
          )}
          <DealerQuote
            onSearchSubmitHandler={onSearchSubmitHandler}
            enrollmentStatus={enrollmentStatus}
            auctionAccessNumber={auctionAccessNumber}
            localDealerNo={localDealerNo}
            onChangeHandler={onChangeHandler}
            showProductSelection={showProductSelection}
            addDealerHandler={addDealerHandler}
            displayDealerConfirm={displayDealerConfirm}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'AUTO PSI',
      render: () => (
        <Tab.Pane>
          <AutoPsi />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'ENROLLMENT STATUS',
      render: () => (
        <Tab.Pane>
          <EnrollmentStatus
            handlePendingAcceptanceLink={handlePendingAcceptanceLink}
          />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className='dealer-enrollment'>
      {loading && (
        <Dimmer active inverted>
          <Loader size='large'>Loading</Loader>
        </Dimmer>
      )}
      {unlinkDealer && (
        <Confirm
          open
          content='Are you sure you want to unlink this dealer?'
          onConfirm={removeDealerHandler}
          onCancel={handleClose}
        />
      )}
      {localDealerName && (
        <Confirm
          open
          content={`Do you want to locally link dealer ${localDealerNo} - ${localDealerName} to ${auctionAccessName}`}
          onConfirm={handleConfirm}
          onCancel={handleClose}
        />
      )}
      {!showProductSelectionWindow && (
        <Tab panes={panes} className='dealer_quote' />
      )}
      {showProductSelectionWindow && (
        <ProductSelection
          handleRadioButtonChange={handleRadioButtonChange}
          handleProductSelection={handleProductSelection}
          onSearchSubmitHandler={onSearchSubmitHandler}
          handleCancel={handleCancel}
          auctionAccessName={auctionAccessName}
          offers={offers}
          phone={phone}
          email={email}
          onChangeHandler={onChangeHandler}
          auctionAccessNumber={auctionAccessNumber}
          selectedDealerNo={selectedDealerNo}
          selectedDealerName={selectedDealerName}
          chosenIndex={chosenIndex}
          chosenOffer={chosenOffer}
          isRevise={isRevise}
          isUpgrade={isUpgrade}
          enrollment={enrollment}
        />
      )}
    </div>
  );
};

DealerDashboard.propTypes = {
  createMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
};

export default compose(
  withRouter,
  graphql(createLocallyLinkedDealerMutation, { name: 'createMutation' }),
  graphql(deleteLocallyLinkedDealerMutation, { name: 'deleteMutation' })
)(DealerDashboard);
